/**
 * UPDATES & BRANDING — what this till runs, what it should run, and the
 * controls that change either.
 *
 * Every control is an endpoint the fleet already uses, through the same
 * confirms: "Update now" and "Install a specific build" are the tier-2
 * `update_now` / `push_version` commands behind `TierTwoConfirm`, exactly as
 * `ActionTiers` issues them; pin, ring and auto-update are the
 * `setDeviceRelease` / `setDeviceAutoUpdate` writes `FleetRowMenu` makes —
 * including `pinnedVersion: null` (never undefined) to release a pin.
 *
 * Branding below is the existing per-device branding tab, unchanged.
 */

import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { FleetActionId } from '@platform/shared-types';
import { useDeviceCommands } from '../../pos/fleet/useDeviceCommands';
import TierTwoConfirm from '../../pos/fleet/TierTwoConfirm';
import { humaniseCommandError } from '../../pos/fleet/FleetDeviceDrawer';
import DeviceBrandingTab from '../../pos/device/DeviceBrandingTab';
import { formatWhen, type DeviceTabProps } from '../../pos/device/deviceDetail';
import { deviceConfigApi } from '../../../services/deviceConfigApi';
import { toastService } from '../../../services/toastService';
import { RubyGemMark } from '../../../components/RubyGemMark';
import { TONE_PILL, updateStatus } from '../fleetStatus';
import { useCanManageFleet } from './useCanManageFleet';

const RINGS: Array<'canary' | 'pilot' | 'stable'> = ['canary', 'pilot', 'stable'];
const DASH = '—';

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-stone-500">{label}</dt>
      <dd className="m-0 min-w-0 break-words text-stone-900">{children}</dd>
    </>
  );
}

const btn = 'inline-flex min-h-[44px] items-center gap-2 rounded-xl px-4 text-[13px] font-semibold disabled:cursor-not-allowed disabled:opacity-45';
const ghost = `${btn} border border-stone-300 bg-white text-stone-800 hover:border-[#DC143C] hover:text-[#DC143C]`;

export default function UpdatesTab(props: DeviceTabProps) {
  const { row, onRefresh } = props;
  const canManage = useCanManageFleet();
  const { issue } = useDeviceCommands(row.deviceId, row.deviceType);
  const [tierTwo, setTierTwo] = useState<FleetActionId | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmOff, setConfirmOff] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const status = updateStatus(row);
  const reachable = !!row.deviceId && !!row.deviceType;
  const locked = !canManage || !reachable;
  const lockTitle = !canManage ? 'You have view-only access to the fleet' : !reachable ? 'This row has never checked in' : undefined;

  const runCommand = async (action: FleetActionId, params?: Record<string, unknown>) => {
    setBusy(action);
    setError(null);
    try {
      const created = await issue(action, params ? { params } : undefined);
      toastService.success(
        created.state === 'queued' && !created.sentAt
          ? `${action.replace(/_/g, ' ')} queued · device offline`
          : `${action.replace(/_/g, ' ')} sent`,
      );
      onRefresh();
    } catch (e: any) {
      setError(humaniseCommandError(e?.message));
    } finally {
      setBusy(null);
    }
  };

  const write = async (key: string, fn: () => Promise<unknown>, done: string) => {
    if (!row.deviceId || !row.deviceType) return;
    setBusy(key);
    setError(null);
    try {
      await fn();
      toastService.success(done);
      onRefresh();
    } catch (e: any) {
      setError(e?.message || 'That change failed');
    } finally {
      setBusy(null);
    }
  };

  const pinCurrent = () => write('pin', () => deviceConfigApi.setDeviceRelease(row.deviceType!, row.deviceId!, { pinnedVersion: row.appVersion! }), `Pinned to ${row.appVersion}`);
  // MUST be `null`, never omitted — JSON drops an undefined key and the old pin survives.
  const releasePin = () => write('unpin', () => deviceConfigApi.setDeviceRelease(row.deviceType!, row.deviceId!, { pinnedVersion: null }), 'Pin released');
  const moveRing = (ring: 'canary' | 'pilot' | 'stable') => write(`ring-${ring}`, () => deviceConfigApi.setDeviceRelease(row.deviceType!, row.deviceId!, { releaseRing: ring }), `Moved to ${ring}`);
  const setAuto = (on: boolean) => write(on ? 'auto-on' : 'auto-off', () => deviceConfigApi.setDeviceAutoUpdate(row.deviceType!, row.deviceId!, on), on ? 'Auto-update turned back on' : 'Auto-update turned off');

  const Busy = ({ k }: { k: string }) => (busy === k ? <RubyGemMark size={14} drawing color="currentColor" /> : null);

  return (
    <div className="grid gap-4">
      <section className="rounded-2xl border border-stone-200 bg-white">
        <div className="border-b border-stone-200 px-4 py-3">
          <h2 className="text-[14px] font-semibold text-stone-900">App updates</h2>
        </div>
        <div className="grid gap-4 px-4 py-4">
          <dl className="m-0 grid grid-cols-1 gap-x-3 gap-y-2 text-[13px] sm:grid-cols-[170px_minmax(0,1fr)]">
            <KV label="Running"><span className="font-mono">{row.appVersion || row.displayVersion || DASH}</span></KV>
            <KV label="Should run"><span className="font-mono">{row.targetVersion || DASH}</span></KV>
            <KV label="Source">
              {row.pinnedVersion
                ? <>Pinned to <span className="font-mono">{row.pinnedVersion}</span>{row.device?.pinnedBy ? ` by ${row.device.pinnedBy}` : ''}{row.device?.pinnedAt ? ` · ${formatWhen(row.device.pinnedAt)}` : ''}</>
                : `${row.releaseRing} ring`}
            </KV>
            <KV label="Channel">{row.assignedChannel || DASH}</KV>
            <KV label="Auto-update">
              <span className={`inline-flex rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${TONE_PILL[row.autoUpdate ? 'ok' : 'warn']}`}>
                {row.autoUpdate ? 'On' : 'Off'}
              </span>
            </KV>
            <KV label="Status">
              <span className={`inline-flex rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${TONE_PILL[status.tone]}`}>{status.label}</span>
              <span className="ml-2 text-stone-500">{status.detail}</span>
            </KV>
          </dl>

          {error && (
            <p role="alert" className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[12.5px] font-semibold text-rose-700">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" /> {error}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={locked || !row.autoUpdate || !!busy}
              title={lockTitle || (!row.autoUpdate ? 'Auto-update is off for this till — turn it back on first' : undefined)}
              onClick={() => setTierTwo('update_now')}
              className={`${btn} bg-[#DC143C] text-white hover:bg-[#9F1239]`}
            >
              <Busy k="update_now" /> Update now
            </button>
            <button type="button" disabled={locked || !!busy} title={lockTitle} onClick={() => setTierTwo('push_version')} className={ghost}>
              <Busy k="push_version" /> Install a specific build…
            </button>
            {row.pinnedVersion ? (
              <button type="button" disabled={locked || !!busy} title={lockTitle} onClick={releasePin} className={ghost}>
                <Busy k="unpin" /> Release pin
              </button>
            ) : row.appVersion ? (
              <button type="button" disabled={locked || !!busy} title={lockTitle} onClick={pinCurrent} className={ghost}>
                <Busy k="pin" /> Pin to current version
              </button>
            ) : null}
            {RINGS.filter((r) => r !== row.releaseRing).map((r) => (
              <button key={r} type="button" disabled={locked || !!busy} title={lockTitle} onClick={() => moveRing(r)} className={ghost}>
                <Busy k={`ring-${r}`} /> Move to {r}
              </button>
            ))}
            {row.autoUpdate ? (
              <button
                type="button"
                disabled={locked || !!busy}
                title={lockTitle}
                onClick={() => setConfirmOff(true)}
                className={`${btn} border border-amber-300 bg-white text-amber-700 hover:bg-amber-50`}
              >
                Turn auto-update off
              </button>
            ) : (
              <button type="button" disabled={locked || !!busy} title={lockTitle} onClick={() => setAuto(true)} className={ghost}>
                <Busy k="auto-on" /> Turn auto-update back on
              </button>
            )}
          </div>

          {confirmOff && (
            /* Custom confirmation — native confirm() is banned platform-wide. */
            <div role="alertdialog" aria-label="Turn auto-update off" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-[13px] font-semibold text-amber-900">
                Turn auto-update off for {row.registerName || row.deviceId}?
              </p>
              <p className="mt-0.5 text-[12.5px] text-amber-800">
                The till keeps its current version and waits for an admin before installing anything. Reversible from here.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={!!busy}
                  onClick={async () => { await setAuto(false); setConfirmOff(false); }}
                  className={`${btn} bg-amber-600 text-white hover:bg-amber-700`}
                >
                  <Busy k="auto-off" /> Turn it off
                </button>
                <button type="button" disabled={!!busy} onClick={() => setConfirmOff(false)} className={ghost}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="grid gap-3">
        <h2 className="text-[14px] font-semibold text-stone-900">Branding on this till</h2>
        <DeviceBrandingTab {...props} />
      </section>

      {tierTwo && (
        <TierTwoConfirm
          row={row}
          action={tierTwo}
          busy={busy === tierTwo}
          onCancel={() => setTierTwo(null)}
          onConfirm={(params) => {
            const action = tierTwo;
            setTierTwo(null);
            void runCommand(action, params);
          }}
        />
      )}
    </div>
  );
}
